import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress, Grid, Link, MenuItem, Paper, TextField, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import {
  CartesianGrid, Label, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { useRackPricing } from '../../context/rackPricing';
import { useAuth } from '../../context/auth';
import millsLogoTransparent from '../../images/mills_logo_transparent.png';
import MillsSalesCta from './MillsSalesCta';

const MILLS_METHODOLOGY_URL = 'https://millsprice.com/methodology';
const MILLS_PRODUCT_CATEGORIES = {
  G: 'Finished gasoline',
  R: 'RFG blendstock',
  C: 'Conventional blendstock',
  U: 'Unblended (ethanol-free) gasoline',
  D: 'Diesel/distillate',
  E: 'Ethanol',
};

const getProductCategoryCode = (series) => (
  series.productCategoryCode || String(series.symbol || '').charAt(3)
);

const useStyles = makeStyles((theme) => ({
  root: { minHeight: 560, minWidth: 1000, padding: theme.spacing(2), boxSizing: 'border-box' },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 2),
  },
  attribution: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    columnGap: theme.spacing(2.5),
    rowGap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'flex-start',
    },
  },
  dataBy: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
    lineHeight: 1,
  },
  dataByLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.65rem',
    opacity: 0.55,
    lineHeight: 1,
    position: 'relative',
    bottom: 8,
  },
  logo: {
    display: 'block',
    width: 82,
    height: 26,
    objectFit: 'cover',
    objectPosition: 'center',
    borderRadius: 2,
    opacity: 0.85,
  },
  methodologyLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    '&:hover': { color: theme.palette.text.primary },
  },
  filters: { marginBottom: theme.spacing(1) },
  select: { minWidth: 220 },
  chart: { height: 440 },
  ctaFooter: { display: 'flex', justifyContent: 'center', marginTop: theme.spacing(1) },
}));

const lineColors = [
  '#fc6c05', '#3f51b5', '#03a9f4', '#4caf50', '#e91e63', '#ffca28',
  '#9c27b0', '#00bcd4', '#8bc34a', '#ff5722', '#607d8b', '#795548',
];

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <Paper style={{ padding: 8 }}>
      <Typography variant="caption" color="textSecondary">{label}</Typography>
      {payload.map((entry) => (
        <Typography key={entry.dataKey} style={{ color: entry.color }}>
          {`${entry.name}: ${Number(entry.value).toFixed(2)} USC/gal`}
        </Typography>
      ))}
    </Paper>
  );
};

export default function RackPriceHistoryChart() {
  const classes = useStyles();
  const theme = useTheme();
  const { millsPricingAccess } = useAuth();
  const {
    rackPricingHistoryCache,
    loadRackPriceHistory,
  } = useRackPricing();
  const dataset = rackPricingHistoryCache.ALL?.data;
  const showSalesCta = Number(millsPricingAccess) !== 1;
  const [cityCode, setCityCode] = useState('');
  const [productCategoryCode, setProductCategoryCode] = useState('ALL');
  const [specCode, setSpecCode] = useState('ALL');
  const [timeCode, setTimeCode] = useState('TR');
  const [loading, setLoading] = useState(!dataset);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    loadRackPriceHistory()
      .catch((loadError) => setError(loadError.message))
      .finally(() => setLoading(false));
  }, [loadRackPriceHistory]);

  const cities = useMemo(() => {
    const cityMap = {};
    (dataset?.series || []).forEach((item) => {
      cityMap[item.cityCode] = {
        code: item.cityCode,
        label: [item.city, item.state].filter(Boolean).join(', '),
      };
    });
    return Object.values(cityMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [dataset]);

  useEffect(() => {
    if (!cityCode && cities.length) setCityCode(cities[0].code);
  }, [cities, cityCode]);

  const citySeries = useMemo(
    () => (dataset?.series || []).filter(
      (item) => cityCode === 'ALL' || item.cityCode === cityCode
    ),
    [dataset, cityCode]
  );

  const productCategoryOptions = useMemo(() => {
    const categoryCodes = new Set(
      citySeries.map(getProductCategoryCode).filter(Boolean)
    );
    return Array.from(categoryCodes)
      .map((code) => ({ code, label: MILLS_PRODUCT_CATEGORIES[code] || code }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [citySeries]);

  useEffect(() => {
    if (
      productCategoryCode !== 'ALL'
      && !productCategoryOptions.some((item) => item.code === productCategoryCode)
    ) setProductCategoryCode('ALL');
  }, [productCategoryCode, productCategoryOptions]);

  const categorySeries = useMemo(
    () => productCategoryCode === 'ALL'
      ? citySeries
      : citySeries.filter((item) => getProductCategoryCode(item) === productCategoryCode),
    [citySeries, productCategoryCode]
  );

  const specificationOptions = useMemo(() => {
    const optionMap = {};
    categorySeries.forEach((item) => {
      optionMap[item.specCode] = {
        code: item.specCode,
        label: item.productName || item.specCode,
      };
    });
    return Object.values(optionMap).sort((a, b) => a.label.localeCompare(b.label));
  }, [categorySeries]);

  useEffect(() => {
    if (
      specCode !== 'ALL'
      && !specificationOptions.some((item) => item.code === specCode)
    ) {
      setSpecCode('ALL');
      if (cityCode === 'ALL') setCityCode(cities[0]?.code || '');
    }
  }, [cities, cityCode, specCode, specificationOptions]);

  const visibleSeries = useMemo(
    () => specCode === 'ALL'
      ? categorySeries
      : categorySeries.filter((item) => item.specCode === specCode),
    [categorySeries, specCode]
  );

  const publicationCodes = useMemo(
    () => timeCode === 'BOTH' ? ['TR', 'TE'] : [timeCode],
    [timeCode]
  );

  const handleSpecificationChange = (event) => {
    const nextSpecCode = event.target.value;
    setSpecCode(nextSpecCode);
    if (nextSpecCode === 'ALL' && cityCode === 'ALL') {
      setCityCode(cities[0]?.code || '');
    }
  };

  const chartData = useMemo(() => {
    if (!dataset) return [];
    const visibleSymbols = new Set(visibleSeries.map((item) => item.symbol));
    const rowsByDate = {};
    (dataset.records || []).forEach((record) => {
      const [recordSymbol, recordTimeCode, assessDate, value] = record;
      const date = assessDate ? assessDate.slice(0, 10) : '';
      if (!publicationCodes.includes(recordTimeCode) || !visibleSymbols.has(recordSymbol)) return;
      if (!rowsByDate[date]) rowsByDate[date] = { date };
      rowsByDate[date][`${recordSymbol}_${recordTimeCode}`] = value;
    });
    return Object.values(rowsByDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [dataset, publicationCodes, visibleSeries]);

  return (
    <Paper className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6">Mills Rack Price — 90-Day History</Typography>
        <div className={classes.attribution}>
          <div className={classes.dataBy}>
            <Typography variant="caption" className={classes.dataByLabel}>Data by</Typography>
            <img src={millsLogoTransparent} alt="Mills" className={classes.logo} />
          </div>
          <Link
            href={MILLS_METHODOLOGY_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            className={classes.methodologyLink}
          >
            View methodology &amp; specifications
            <OpenInNewIcon style={{ fontSize: 15, marginLeft: 4, verticalAlign: 'text-bottom' }} />
          </Link>
        </div>
      </div>
      <Grid container spacing={2} alignItems="center" className={classes.filters}>
        <Grid item>
          <TextField
            select
            label="Rack City"
            value={cityCode}
            onChange={(event) => setCityCode(event.target.value)}
            className={classes.select}
            SelectProps={{
              renderValue: (value) => value === 'ALL'
                ? 'All Cities'
                : cities.find((city) => city.code === value)?.label || value,
            }}
          >
            {specCode !== 'ALL' && <MenuItem value="ALL">All Cities</MenuItem>}
            {cities.map((city) => <MenuItem key={city.code} value={city.code}>{city.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item>
          <TextField
            select
            label="Product Category"
            value={productCategoryCode}
            onChange={(event) => setProductCategoryCode(event.target.value)}
            className={classes.select}
          >
            <MenuItem value="ALL">All Product Categories</MenuItem>
            {productCategoryOptions.map((item) => (
              <MenuItem key={item.code} value={item.code}>{item.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item>
          <TextField select label="Specification" value={specCode} onChange={handleSpecificationChange} className={classes.select}>
            {cityCode !== 'ALL' && <MenuItem value="ALL">All Specifications</MenuItem>}
            {specificationOptions.map((item) => (
              <MenuItem key={item.code} value={item.code}>{item.label} ({item.code})</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item>
          <TextField select label="Publication" value={timeCode} onChange={(event) => setTimeCode(event.target.value)}>
            <MenuItem value="TR">Morning (TR)</MenuItem>
            <MenuItem value="TE">Evening (TE)</MenuItem>
            <MenuItem value="BOTH">Morning &amp; Evening</MenuItem>
          </TextField>
        </Grid>
      </Grid>
      <div className={classes.chart}>
        {loading ? (
          <Grid container alignItems="center" justifyContent="center" style={{ height: '100%' }}><CircularProgress /></Grid>
        ) : error ? (
          <Grid container alignItems="center" justifyContent="center" style={{ height: '100%' }}><Typography color="error">{error}</Typography></Grid>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 15, right: 25, bottom: 45, left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="date" stroke={theme.palette.text.secondary} />
              <YAxis stroke={theme.palette.text.secondary} domain={['auto', 'auto']}>
                <Label angle={270} position="left" offset={5} style={{ textAnchor: 'middle', fill: theme.palette.text.primary }}>
                  MARC Price (USC/gal)
                </Label>
              </YAxis>
              <Tooltip content={<ChartTooltip />} />
              <Legend />
              {visibleSeries.map((item, seriesIndex) => publicationCodes.map((publicationCode, publicationIndex) => {
                const seriesName = cityCode === 'ALL'
                  ? [item.city, item.state].filter(Boolean).join(', ')
                  : item.productName;
                const publicationName = publicationCode === 'TR' ? 'Morning' : 'Evening';
                return (
                  <Line
                    key={`${item.symbol}_${publicationCode}`}
                    type="monotone"
                    dataKey={`${item.symbol}_${publicationCode}`}
                    name={timeCode === 'BOTH' ? `${seriesName} — ${publicationName}` : seriesName}
                    stroke={lineColors[(seriesIndex * publicationCodes.length + publicationIndex) % lineColors.length]}
                    strokeWidth={2}
                    strokeDasharray={timeCode === 'BOTH' && publicationCode === 'TE' ? '6 3' : undefined}
                    dot={false}
                    connectNulls
                  />
                );
              }))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
      {showSalesCta && (
        <div className={classes.ctaFooter}>
          <MillsSalesCta />
        </div>
      )}
    </Paper>
  );
}
